import { createSlice } from '@reduxjs/toolkit';

const readMediaPreference = (key, fallback = true) => {
  try {
    const value = sessionStorage.getItem(key);
    return value === null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
};

const persistMediaPreference = (key, enabled) => {
  try {
    sessionStorage.setItem(key, String(enabled === true));
  } catch {}
};

const SCREEN_SHARE_LAYOUT_KEY = 'meeting.screenShareLayout';
const SCREEN_SHARE_LAYOUTS = new Set(['split', 'focus']);

const readScreenShareLayout = () => {
  try {
    const stored = localStorage.getItem(SCREEN_SHARE_LAYOUT_KEY);
    return SCREEN_SHARE_LAYOUTS.has(stored) ? stored : 'split';
  } catch {
    return 'split';
  }
};

const persistScreenShareLayout = (layout) => {
  try {
    localStorage.setItem(SCREEN_SHARE_LAYOUT_KEY, layout);
  } catch {}
};

const initialState = {
  transports: [],
  producers: [],
  consumers: [],
  localStream: null,
  remoteStreams: {},
  remoteScreens: {},
  audioEnabled: readMediaPreference('meeting.micEnabled'),
  videoEnabled: readMediaPreference('meeting.cameraEnabled'),
  screenSharing: false,
  screenShareLayout: readScreenShareLayout(),
  handRaised: false,
  loading: false,
  error: null,
  remoteVideoStatus: {}, 
  recording: false,
  showMoreStatus: false,
  showInfoStatus: false,
  chatStatus: false,
  participantsStatus: false,
};

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    addTransport: (state, action) => {
      state.transports.push(action.payload);
    },
    addProducer: (state, action) => {
      state.producers.push(action.payload);
    },
    addConsumer: (state, action) => {
      state.consumers.push(action.payload);
    },
    setLocalStream: (state, action) => {
      const stream = action.payload;
      if (stream) {
        const tracks = stream.getTracks();
        state.localStream = new MediaStream(tracks); // force new reference
      } else {
        state.localStream = null;
      }
    },
    
    addRemoteStream: (state, action) => {
      const { userId, kind, stream } = action.payload;
      if (!state.remoteStreams[userId]) {
        state.remoteStreams[userId] = {
          audio: null,
          video: null,
          screen: null
        };
      }
      state.remoteStreams[userId][kind] = stream;
    },
    toggleAudio: (state, action) => {
      const enable = action.payload;
      state.audioEnabled = enable;
      persistMediaPreference('meeting.micEnabled', enable);
      
      // Update track if exists
      const audioTrack = state.localStream?.getAudioTracks()?.[0];
      if (audioTrack && audioTrack.readyState === 'live') {
        audioTrack.enabled = enable;
      }
    },
    toggleVideo: (state, action) => {
      state.videoEnabled = action.payload;
      persistMediaPreference('meeting.cameraEnabled', action.payload);
      const track = state.localStream?.getVideoTracks?.()[0];
      if (track && track.readyState === 'live') {
        track.enabled = action.payload;
      }
    },
    
    updateRemoteVideoStatus: (state, action) => {
      const { sessionId, isVideoEnabled } = action.payload;
      state.remoteVideoStatus[sessionId] = isVideoEnabled;
    },
    toggleRecording: (state, action) => {
      state.recording = action.payload;
    },
    toggleScreenSharing: (state, action) => {
      state.screenSharing = action.payload;
    },
    setScreenShareLayout: (state, action) => {
      const layout = SCREEN_SHARE_LAYOUTS.has(action.payload)
        ? action.payload
        : 'split';
      state.screenShareLayout = layout;
      persistScreenShareLayout(layout);
    },
    toggleHandRaised: (state, action) => {
      state.handRaised = action.payload;
    },
    toggleMeetInfo: (state, action) => {
      state.showInfoStatus = action.payload;
      state.participantsStatus =false
      state.chatStatus =false
    },
    toggleShowmore: (state, action) => {
      state.showMoreStatus = action.payload;
    },
    toggleChatStatus: (state, action) => {
      state.chatStatus = action.payload;
      state.showInfoStatus = false;
      state.participantsStatus =false
    },
    toggleParticipantsStatus: (state, action) => {
      state.participantsStatus = action.payload;
      state.showInfoStatus = false;
      state.chatStatus =false
    },
     // In your mediaSlice.js
addRemoteScreen: (state, { payload }) => {
  const sessionId = payload?.sessionId || payload?.userId;
  const userId = payload?.userId || sessionId;
  const source = payload?.track || payload?.stream;
  const track = source?.getVideoTracks?.()[0] || source;
  if (!sessionId || !track?.id) return;
  const key = String(sessionId);
  if (!state.remoteScreens[key]) {
    state.remoteScreens[key] = { userId, sessionId, stream: new MediaStream() };
  }
  const already = state.remoteScreens[key].stream.getVideoTracks()
                   .some(t => t.id === track.id);
  if (!already) state.remoteScreens[key].stream.addTrack(track);
},
removeRemoteScreen: (state, { payload }) => {
  const sessionId = payload?.sessionId || payload?.userId;
  if (!sessionId) return;
  delete state.remoteScreens[String(sessionId)];
},

    
    
    
    clearMedia: (state) => {
      state.transports = [];
      state.producers = [];
      state.consumers = [];
      state.localStream = null;
      state.remoteStreams = {};
      state.remoteScreens = {};
      // Keep the user's last mic/camera choice for prejoin and rejoin. Media
      // tracks are cleared above, but privacy intent must not reset to ON.
      state.screenSharing = false;
      state.handRaised = false;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  addTransport,
  addProducer,
  addConsumer,
  setLocalStream,
  addRemoteStream,
  toggleAudio,
  toggleVideo,
  updateRemoteVideoStatus,
  toggleScreenSharing,
  setScreenShareLayout,
  toggleHandRaised,
  clearMedia,
  toggleRecording,
  toggleMeetInfo,
  toggleShowmore,
  toggleChatStatus,
  addRemoteScreen,
  removeRemoteScreen,
  toggleParticipantsStatus
} = mediaSlice.actions;

export default mediaSlice.reducer;
